import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Cpu } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import SavingsCalculator from "@/components/SavingsCalculator";
import Icon from "@/components/Icon";
import { IMAGES, SERVICES, COMPONENTS, BRANDS } from "@/data/site";

export default function Services() {
  return (
    <div data-testid="page-services">
      <PageHero
        crumb="Layanan & Produk"
        eyebrow="Layanan & Produk"
        title="Complete solar PV systems across various scales"
        desc="We design and deliver high-quality, safe, off-grid and grid-connected solar PV solutions to where they are needed most."
        image={IMAGES.rooftopCommercial}
      />

      {/* SERVICES ALTERNATING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 space-y-20 sm:space-y-28">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.id}
            id={s.id}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className={`grid gap-10 lg:grid-cols-2 lg:items-center ${
              i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
            }`}
          >
            <div className="relative overflow-hidden rounded-3xl shadow-xl">
              <img
                src={IMAGES[s.img]}
                alt={s.title}
                className="h-72 w-full object-cover sm:h-96"
              />
              <span className="absolute top-4 left-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/90 text-blue-900 backdrop-blur">
                <Icon name={s.icon} className="h-6 w-6" />
              </span>
            </div>
            <div>
              <p className="aje-eyebrow mb-3">Layanan {String(i + 1).padStart(2, "0")}</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                {s.title}
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                {s.detail}
              </p>
              <Link
                to="/kontak"
                data-testid={`service-cta-${s.id}`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
              >
                Book Your Free Consultation
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        ))}
      </section>

      {/* COMPONENTS */}
      <section className="bg-slate-50 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            center
            eyebrow="System Components"
            title="Complete Solar Component Ranges for On-Grid & Off-Grid Applications"
            desc="Comprehensive solutions with guaranteed quality for long-term reliability."
          />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COMPONENTS.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
              >
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                <span className="font-medium text-slate-800">{c}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-900 text-white">
                <Cpu className="h-5 w-5" />
              </span>
              <h3 className="font-display text-xl font-bold text-slate-900">
                Tier-1 Brands We Use
              </h3>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {BRANDS.map((b) => (
              <img
             key={b.name}
             src={b.logo}
              alt={b.name}
             className="h-10 sm:h-12 w-auto object-contain transition-transform duration-300 hover:scale-110"
             />
              ))}
              <span className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                Mounting Aluminium T6 Anti-Korosi
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SectionHeading
              eyebrow="Fast Estimation"
              title="Calculate Your Savings Potential"
              desc="Get a quick overview of your solar potential, savings, payback period, and CO₂ reduction in just a few seconds."
            />
            <Link
              to="/kontak"
              data-testid="services-consult-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-blue-900 px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-blue-800 hover:-translate-y-0.5"
            >
              Request Feasibility Study <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <SavingsCalculator />
        </div>
      </section>
    </div>
  );
}
